import { justDate, addDays, startOfWeek, isoDateString } from './date-utils';

// Generate calendar grid for month view (42 days, 6 weeks)
export function generateCalendarGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  
  // Find Sunday of the week containing the 1st
  const gridStart = startOfWeek(firstDay);
  
  // Generate 6 weeks (42 days)
  const days = [];
  let current = new Date(gridStart);
  
  for (let i = 0; i < 42; i++) {
    days.push(new Date(current));
    current = addDays(current, 1);
  }
  
  return days;
}

// Get week number for a date (ISO week)
export function getWeekNumber(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return weekNo;
}

// Get all dates in a week (Sun-Sat)
export function getWeekDates(date) {
  const weekStart = startOfWeek(date);
  const dates = [];
  
  for (let i = 0; i < 7; i++) {
    dates.push(addDays(weekStart, i));
  }
  
  return dates;
}

// Get all dates in a month
export function getMonthDates(year, month) {
  const dates = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  let current = new Date(firstDay);
  
  while (current <= lastDay) {
    dates.push(new Date(current));
    current = addDays(current, 1);
  }
  
  return dates;
}

// Check if date is in current month
export function isInMonth(date, month, year) {
  const d = new Date(date);
  return d.getMonth() === month && d.getFullYear() === year;
}

// Group dates by week
export function groupDatesByWeek(dates) {
  const weeks = [];
  let currentWeek = [];
  
  dates.forEach((date, index) => {
    currentWeek.push(date);
    
    // Sunday is end of week (day 0)
    if (new Date(date).getDay() === 6 || index === dates.length - 1) {
      weeks.push([...currentWeek]);
      currentWeek = [];
    }
  });
  
  return weeks;
}

// Get date range as array
export function getDateRange(startDate, endDate) {
  const dates = [];
  let current = justDate(startDate);
  const end = justDate(endDate);
  
  while (current <= end) {
    dates.push(new Date(current));
    current = addDays(current, 1);
  }
  
  return dates;
}

// Check if two date ranges overlap
export function dateRangesOverlap(start1, end1, start2, end2) {
  const s1 = justDate(start1);
  const e1 = justDate(end1);
  const s2 = justDate(start2);
  const e2 = justDate(end2);
  
  return s1 <= e2 && s2 <= e1;
}

// Get month name and year string
export function getMonthYearString(date) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
}

// Get short month name
export function getShortMonthName(date) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short'
  });
}

// Get day name
export function getDayName(date) {
  return new Date(date).toLocaleDateString('en-US', {
    weekday: 'long'
  });
}

// Get short day name
export function getShortDayName(date) {
  return new Date(date).toLocaleDateString('en-US', {
    weekday: 'short'
  });
}

// Group jobs by date
export function groupJobsByDate(jobs) {
  const grouped = {};
  
  jobs.forEach(job => {
    const dates = getJobDates(job);
    dates.forEach(dateInfo => {
      const dateKey = isoDateString(dateInfo.date);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      
      // Attach workDay status to job for rendering
      grouped[dateKey].push({
        ...job,
        _workDayStatus: dateInfo.status
      });
    });
  });
  
  return grouped;
}

// Helper to extract dates from job (handles both workDays and legacy)
function getJobDates(job) {
  // New explicit workDays model - includes status
  if (job.workDays && job.workDays.length > 0) {
    return job.workDays
      .filter(wd => wd.status !== 'noWork' && wd.status !== 'cancelled')
      .map(wd => ({
        date: justDate(wd.date),
        status: wd.status || 'planned'
      }));
  }
  
  // Legacy: scheduledDate only
  if (job.scheduledDate) {
    return [{
      date: justDate(job.scheduledDate),
      status: 'planned'
    }];
  }
  
  return [];
}
// Imported .ics events → { 'YYYY-MM-DD': [events] }, spread across every day
// each event covers. All-day end dates are exclusive; a timed event ending
// exactly at midnight doesn't spill into the next day. All-day events sort
// first, then by start time.
export function groupEventsByDate(events) {
  const byDate = {};
  events.forEach(event => {
    if (!event.start) return;
    const end = event.end && event.end > event.start ? event.end : event.start;
    const endsAtMidnight = end > event.start && end.getHours() === 0 && end.getMinutes() === 0;
    const lastDay = justDate(event.allDay || endsAtMidnight ? new Date(end.getTime() - 1) : end);
    for (let d = justDate(event.start); d <= lastDay; d = addDays(d, 1)) {
      const key = isoDateString(d);
      (byDate[key] = byDate[key] || []).push(event);
    }
  });
  Object.values(byDate).forEach(list => list.sort((a, b) => (b.allDay - a.allDay) || (a.start - b.start)));
  return byDate;
}
